import React, { useState } from "react";
import { ArrowUpRight, ArrowRight, CloudHail, Leaf, Scale, BookOpen, ChevronDown, Stamp } from "lucide-react";
import Reveal from "./Reveal";
import TechModal from "./TechModal";
import StudyModal from "./StudyModal";
import { technology, brand, techModal } from "../mock/content";
import { t } from "../lib/ui";
import LazyVideo from "./LazyVideo";
import { VestaTechBlock } from "./vesta/VestaTech";

const TECH_ANCHORS = { "PRISMA 4.0": "tech-prisma-4-0", "X-CORE²": "tech-x-core-2", "ECO-RED": "tech-eco-red", "AURA": "tech-aura" };
const GOLD = "linear-gradient(135deg,#F6E27A 0%,#D4AF37 45%,#A67C00 100%)";
const SILVER = "linear-gradient(135deg,#F2F2F2 0%,#CBCBCB 45%,#A3A3A3 100%)";
const TECH_METAL = {
  "X-CORE²": { grad: GOLD, bar: "#D4AF37", glow: "212,175,55", titleColor: "#D4AF37", badgeColor: "#4a3600", badge: "tech_badge_preview", flag: true, patent: true },
  "PRISMA 4.0": { grad: SILVER, bar: "#C7C7C7", glow: "203,203,203", titleColor: "#DcDcDc", badgeColor: "#2b2b2b", badge: "tech_badge_dev", flag: true },
  "ECO-RED": { grad: SILVER, bar: "#C7C7C7", glow: "203,203,203", titleColor: "#DcDcDc", badgeColor: "#2b2b2b", badge: "tech_badge_style", flag: true },
  "AURA": { grad: SILVER, bar: "#C7C7C7", glow: "203,203,203", titleColor: "#DcDcDc", badgeColor: "#2b2b2b", badge: "tech_badge_aura", flag: false },
};
const XCoreIcon = ({ size = 20, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
    <path d="M4 8 Q12 20 20 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
    <circle cx="4" cy="8" r="1.9" fill="currentColor" />
    <circle cx="12" cy="14" r="1.9" fill="currentColor" />
    <circle cx="20" cy="8" r="1.9" fill="currentColor" />
  </svg>
);

const TECH_ICONS = { "PRISMA 4.0": CloudHail, "X-CORE²": XCoreIcon, "ECO-RED": Leaf, "AURA": Scale };
const TECH_CAPTIONS = { "PRISMA 4.0": "tech_cap_prisma", "X-CORE²": "tech_cap_xcore", "ECO-RED": "tech_cap_ecored", "AURA": "tech_cap_aura" };
const TECH_VIDEO = { "PRISMA 4.0": "/tech/prisma-hail.mp4" };
const TECH_DETAIL = {
  "PRISMA 4.0": "/img/ext/xau4jdab_IMG_5960-a5526b.webp",
  "X-CORE²": "/img/ext/k5any45o_IMG_8500-3ed7d2.webp",
  "ECO-RED": "/img/ext/09p6bo86_IMG_4901-114980.webp",
  "AURA": "/media/IMG_5876.jpeg",
};

export default function TechnologySection({ irsBordered = false, showIntro = true, showIrs = true, irsOnly = false, showStudy = true, cardsLarge = false, mediaTall = false, collapsibleCalc = false, collapsibleStudy = false, tightTop = false, showClaim = true, mobileSlider = false, claimDesktopOnly = false, showVesta = false }) {
  const max = Math.max(...technology.irs.map((i) => i.value));
  const [activeTech, setActiveTech] = useState(null);
  const [slide, setSlide] = useState(0);
  const onSlideScroll = (e) => {
    const el = e.currentTarget;
    const w = el.firstElementChild?.offsetWidth || el.clientWidth;
    setSlide(Math.round(el.scrollLeft / (w + 16)));
  };
  const [calcOpen, setCalcOpen] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [peso, setPeso] = useState("");
  const [watt, setWatt] = useState("");
  const [materiale, setMateriale] = useState("polimero");
  const p = parseFloat(peso);
  const w = parseFloat(watt);
  const valid = p > 0 && w > 0;
  const factor = materiale === "vetro" ? 0.5 : 0.95;
  const irs = valid ? (p * 1000 / w) * factor : null;
  const mediaHeightClass = cardsLarge ? "h-[269px] lg:h-[346px]" : mediaTall ? "h-[184px] lg:h-[202px]" : "h-40 lg:h-44";
  const mediaWidthClass = cardsLarge ? "w-[95%] mx-auto" : "w-full";

  let color = "#ffffff";
  let verdict = "";
  if (irs !== null) {
    if (irs < 30) { color = "#ef4444"; verdict = t("verdict_fragile"); }
    else if (irs <= 50) { color = brand.yellow; verdict = t("verdict_standard"); }
    else { color = "#22c55e"; verdict = t("verdict_robust"); }
  }

  const factorNote = (
    <div className="space-y-3 text-sm" style={{ fontFamily: "Space Grotesk" }}>
      <p className="text-white/60 uppercase tracking-[0.14em] text-xs">{t("factor_label")}</p>
      <p className="text-white/60 leading-relaxed">
        <span className="font-bold" style={{ color: brand.yellow }}>×0,95</span>{t("factor_p1")}
      </p>
      <p className="text-white/60 leading-relaxed">
        <span className="font-bold" style={{ color: brand.yellow }}>×0,5</span>{t("factor_p2")}
      </p>
    </div>
  );

  return (
    <section id={irsOnly ? "irs" : "tecnologia"} className={`bg-black text-white ${tightTop ? "pt-[10px] md:pt-16" : irsOnly ? "pt-[17px] md:pt-[49px] lg:pt-[17px]" : "pt-12 md:pt-40"} pb-16 md:pb-24 relative overflow-hidden`}>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
        {showIntro && !irsOnly && (
        <>
        {showClaim && (
        <div className={`${claimDesktopOnly ? "hidden md:grid" : "grid"} lg:grid-cols-12 gap-4 lg:gap-12 items-center mb-16 lg:mb-24`}>
          <div className="lg:col-span-7 order-2 lg:order-1 mt-0 lg:mt-0">
            <Reveal>
              <p data-testid="tech-hashtag-title" className="font-black leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" style={{ fontFamily: "Archivo" }}>
                <span style={{ color: brand.yellow }}>#pazzi</span><span className="text-white">abbastanza</span>
              </p>
              <p className="mt-4 text-white/95 text-lg sm:text-xl lg:text-2xl font-medium" style={{ fontFamily: "Space Grotesk" }}>
                {t("tech_claim_sub")}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal delay={0.1}>
              <div className="lg:border-l-2 lg:border-[#F9E212] lg:pl-8">
                <p className="text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed whitespace-pre-line" style={{ fontFamily: "Inter" }}>
                  {technology.body.split("\n").map((line, li, arr) => {
                    const isLast = li === arr.length - 1;
                    const content = line.split(/(#[^\s,.;:!?]+)/g).map((part, i) =>
                      /^#/.test(part)
                        ? <span key={i} className="font-black" style={{ color: isLast ? "#111" : brand.yellow, fontFamily: "Archivo" }}>{part}</span>
                        : part
                    );
                    return (
                      <React.Fragment key={li}>
                        {li > 0 && "\n"}
                        {isLast
                          ? <mark data-testid="tech-highlight" className="px-1.5 py-0.5 rounded-sm font-medium" style={{ background: brand.yellow, color: "#111", WebkitBoxDecorationBreak: "clone", boxDecorationBreak: "clone" }}>{content}</mark>
                          : content}
                      </React.Fragment>
                    );
                  })}
                </p>
                {technology.body2 && <p className="mt-5 text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed whitespace-pre-line" style={{ fontFamily: "Inter" }}>
                  {technology.body2.split(/(#[^\s,.;:!?]+)/g).map((part, i) =>
                    /^#/.test(part)
                      ? <span key={i} className="font-black" style={{ color: brand.yellow, fontFamily: "Archivo" }}>{part}</span>
                      : part
                  )}
                </p>}
                {technology.body2 && !technology.body2.includes("#") && (
                  <span className="mt-7 inline-block font-black tracking-tight text-3xl md:text-4xl" style={{ color: brand.yellow, fontFamily: "Archivo" }}>
                    {t("tech_hashtag")}
                  </span>
                )}
              </div>
            </Reveal>
          </div>
        </div>
        )}

        <div className={`${showClaim ? (claimDesktopOnly ? "mt-6 md:mt-20 lg:mt-32" : "mt-20 lg:mt-32") : "mt-10 lg:mt-16"} mb-12 lg:mb-16`}>
          <Reveal>
            <span className="block text-white/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>
              {technology.eyebrow}
            </span>
            <h2 className="mt-4 font-black leading-[0.9] tracking-tight" style={{ fontFamily: "Archivo", fontSize: "clamp(2.4rem, 5.2vw, 4.5rem)" }}>
              Designed for <span style={{ color: brand.yellow }}>{t("tech_h_accent")}</span>
            </h2>
          </Reveal>
        </div>
        </>
        )}

        {!irsOnly && (
        <>
        <div
          onScroll={mobileSlider ? onSlideScroll : undefined}
          className={mobileSlider
            ? `flex md:grid md:grid-cols-2 ${cardsLarge ? "lg:grid-cols-2" : "lg:grid-cols-4"} gap-4 md:gap-8 lg:gap-[30px] overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 pt-3 md:pt-0 pb-2 md:pb-0 scrollbar-none`
            : `grid md:grid-cols-2 ${cardsLarge ? "lg:grid-cols-2" : "lg:grid-cols-4"} gap-8 lg:gap-[30px]`}
          data-testid={mobileSlider ? "tech-mobile-slider" : undefined}
        >
          {technology.items.map((item, i) => {
            const anchor = TECH_ANCHORS[item.code];
            const Icon = TECH_ICONS[item.code];
            const caption = TECH_CAPTIONS[item.code];
            const hasModal = !!techModal[item.code];
            const metal = TECH_METAL[item.code];
            const inner = (
              <div id={anchor} className={`group relative scroll-mt-28 ${cardsLarge ? "p-10 lg:p-14" : "p-8"} border border-white/10 h-full flex flex-col hover:bg-white/[0.03] transition-colors`} style={metal ? { background: `linear-gradient(180deg, rgba(${metal.glow},0.09), rgba(${metal.glow},0) 42%)`, boxShadow: `inset 0 3px 0 0 ${metal.bar}` } : undefined}>
                {metal?.badge && (
                  <span className="absolute top-0 left-5 md:left-6 -translate-y-1/2 z-10 inline-flex items-center gap-1.5 text-[11px] md:text-[11px] uppercase tracking-[0.1em] md:tracking-[0.16em] font-bold px-2.5 py-1 leading-none shadow-sm whitespace-nowrap" style={{ fontFamily: "Space Grotesk", background: metal.grad, color: metal.badgeColor }} data-testid={`tech-badge-${anchor}`}>
                    {metal.flag && (
                      <span className="flex h-2.5 w-4 overflow-hidden rounded-[1px] shrink-0 ring-1 ring-black/20">
                        <span className="flex-1" style={{ background: "#008C45" }} />
                        <span className="flex-1" style={{ background: "#F4F5F0" }} />
                        <span className="flex-1" style={{ background: "#CD212A" }} />
                      </span>
                    )}
                    {t(metal.badge)}
                  </span>
                )}
                {metal?.patent && (
                  <span className="relative self-start -mt-2 mb-4 z-10 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] md:tracking-[0.16em] font-bold px-2.5 py-1 leading-none shadow-sm whitespace-nowrap text-black" style={{ fontFamily: "Space Grotesk", background: brand.yellow }} data-testid={`tech-patent-badge-${anchor}`}>
                    <Stamp size={11} strokeWidth={2.5} /> {t("tech_patent_badge")}
                  </span>
                )}
                {Icon && (
                  <div className="flex items-center justify-end gap-2 mb-5">
                    <span className="text-[11px] uppercase tracking-[0.08em] md:tracking-[0.16em] text-white/60 text-right" style={{ fontFamily: "Space Grotesk" }}>{t(caption)}</span>
                    <Icon size={cardsLarge ? 24 : 20} strokeWidth={1.6} className="shrink-0" style={{ color: brand.yellow }} />
                  </div>
                )}
                <h3 className={`font-black tracking-tight ${cardsLarge ? "text-4xl lg:text-5xl" : "text-3xl"}`} style={metal ? { fontFamily: "Archivo", background: metal.grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" } : { fontFamily: "Archivo" }}>{item.code}</h3>
                <p className={`font-semibold ${cardsLarge ? "mt-3 text-base lg:text-lg" : "mt-2 text-sm"}`} style={{ color: metal ? metal.titleColor : brand.yellow, fontFamily: "Space Grotesk" }}>{item.claim}</p>
                <p className={`text-white/55 leading-relaxed ${cardsLarge ? "mt-5 mb-8 text-base" : "mt-4 mb-6 text-sm"}`} style={{ fontFamily: "Inter" }}>{item.desc}</p>
                {TECH_DETAIL[item.code] && (
                  <div className={`relative overflow-hidden border-t border-white/10 mt-auto ${cardsLarge ? "-mx-10 lg:-mx-14" : "-mx-8"}`}>
                    <div className={item.code === "X-CORE²" ? "hero-slide-in" : ""}>
                      {TECH_VIDEO[item.code] ? (
                        <LazyVideo
                          src={TECH_VIDEO[item.code]}
                          autoPlay
                          muted
                          loop
                          playsInline
                          poster={TECH_DETAIL[item.code]}
                          className={`${mediaWidthClass} object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500 ${mediaHeightClass}`}
                          style={item.code === "PRISMA 4.0" ? { objectPosition: "center 28%" } : undefined}
                          data-testid={`tech-card-video-${anchor}`}
                        />
                      ) : (
                        <img loading="lazy" decoding="async"
                          src={TECH_DETAIL[item.code]}
                          alt={`Dettaglio ${item.code}`}
                          className={`${mediaWidthClass} object-cover opacity-85 group-hover:opacity-100 transition-all duration-500 ${mediaHeightClass} ${item.code === "X-CORE²" ? "origin-top scale-[1.55] group-hover:scale-[1.65] sm:origin-center sm:scale-100 sm:group-hover:scale-105" : "group-hover:scale-105"}`}
                          style={item.code === "PRISMA 4.0" ? { objectPosition: "center 28%" } : item.code === "X-CORE²" ? { objectPosition: "50% 0%", filter: "saturate(1.55) brightness(1.06) contrast(1.04)" } : undefined}
                        />
                      )}
                    </div>
                    {item.code === "X-CORE²" && <div className="hero-glint" aria-hidden="true" />}
                  </div>
                )}
                <div className="mt-6 flex items-center gap-2 text-white/60 group-hover:text-white transition-colors">
                  <span className="text-xs md:text-sm uppercase tracking-[0.05em] font-bold whitespace-nowrap" style={{ fontFamily: "Arial, sans-serif" }}>{t("tech_enter")} {item.code}</span>
                  <ArrowUpRight className="shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={cardsLarge ? 22 : 18} />
                </div>
              </div>
            );
            return (
              <Reveal key={item.code} delay={0.06 * i} className={mobileSlider ? "shrink-0 w-[86%] snap-center md:w-auto" : ""}>
                {hasModal ? (
                  <button type="button" onClick={() => setActiveTech(item.code)} className="block w-full text-left h-full" data-testid={`tech-card-${anchor}`}>{inner}</button>
                ) : inner}
              </Reveal>
            );
          })}
        </div>
        {mobileSlider && (
          <div className="md:hidden mt-5 flex items-center gap-2" data-testid="tech-mobile-dots">
            {technology.items.map((item, i) => (
              <span key={item.code} className="h-1.5 transition-[width,background-color] duration-300" style={{ width: i === slide ? 32 : 12, background: i === slide ? brand.yellow : "rgba(255,255,255,0.25)" }} />
            ))}
            <span className="ml-2 text-xs text-white/50 tabular-nums" style={{ fontFamily: "Space Grotesk" }} data-testid="tech-mobile-counter">{Math.min(slide + 1, technology.items.length)} / {technology.items.length}</span>
          </div>
        )}
        {showVesta && <VestaTechBlock large={cardsLarge} live={showVesta === "live"} />}
        </>
        )}

        {showIrs && (
        <div
          className={irsBordered ? `relative mt-12 lg:mt-20 p-5 pt-12 md:p-10 lg:p-12${(collapsibleCalc || collapsibleStudy) ? " pb-16 md:pb-16" : ""}` : "mt-12 lg:mt-20"}
          style={irsBordered ? { border: `1px solid ${brand.yellow}` } : undefined}
          data-testid="irs-concept"
        >
          {irsBordered && (
            <span
              className="absolute top-0 -translate-y-1/2 left-4 right-4 md:left-10 md:right-auto lg:left-12 bg-black px-3 md:px-4 text-[11px] leading-tight md:text-2xl font-bold uppercase tracking-[0.12em] md:tracking-[0.18em] md:whitespace-nowrap"
              style={{ color: brand.yellow, fontFamily: "Space Grotesk" }}
              data-testid="irs-perimeter-label"
            >
              {t("irs_perimeter")}
            </span>
          )}
          {irsBordered && collapsibleCalc && (
            <button
              type="button"
              onClick={() => setCalcOpen((o) => !o)}
              aria-expanded={calcOpen}
              className="group absolute bottom-0 translate-y-1/2 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 bg-black px-4 md:px-5 inline-flex items-center justify-center gap-2 text-[11px] md:text-lg font-bold uppercase tracking-[0.14em] md:tracking-[0.18em] whitespace-nowrap transition-colors hover:text-white"
              style={{ color: brand.yellow, fontFamily: "Space Grotesk" }}
              data-testid="irs-calc-toggle"
            >
              {calcOpen ? t("irs_close_calc") : t("irs_calc")}
              <ChevronDown size={16} strokeWidth={3} className={`transition-transform duration-300 ${calcOpen ? "rotate-180" : ""}`} />
            </button>
          )}
          {irsBordered && collapsibleStudy && (
            <button
              type="button"
              onClick={() => setStudyOpen(true)}
              className="group absolute bottom-0 translate-y-1/2 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 bg-black px-4 md:px-5 inline-flex items-center justify-center gap-2 text-[11px] md:text-lg font-bold uppercase tracking-[0.14em] md:tracking-[0.18em] whitespace-nowrap transition-colors hover:text-white"
              style={{ color: brand.yellow, fontFamily: "Space Grotesk" }}
              data-testid="irs-study-toggle"
            >
              <span className="irs-study-pulse inline-flex items-center gap-2" data-testid="irs-study-pulse">
              {t("irs_study_toggle")}
              <ArrowUpRight size={16} strokeWidth={3} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          )}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start lg:items-stretch">
          <div className="lg:col-span-5">
            <Reveal>
              <h3 className="font-black text-3xl md:text-4xl leading-tight tracking-tight" style={{ fontFamily: "Archivo" }}>
                IRS.<br />{t("irs_title2")}
              </h3>
              <p className="mt-5 text-white/55 leading-relaxed" style={{ fontFamily: "Inter" }}>
                {t("irs_p1")}
              </p>
              <p className="mt-3 font-semibold inline-flex items-center gap-2" style={{ color: brand.yellow, fontFamily: "Space Grotesk" }}>
                {t("irs_more_a")} <ArrowRight size={18} strokeWidth={3} /> {t("irs_more_b")}
              </p>
              <p className="mt-5 text-white/55 leading-relaxed" style={{ fontFamily: "Inter" }}>
                {t("irs_p2")}
              </p>
              <div className="lg:hidden mt-8 overflow-hidden max-w-sm mx-auto" data-testid="irs-image-mobile">
                <img loading="lazy" decoding="async"
                  src="/img/ext/ciueoq69_IMG_5864-5439e3.webp"
                  alt="IRS frontale — stratigrafia del pannello fotovoltaico"
                  className="w-full h-auto object-contain transition-[filter] duration-500 hover:[filter:sepia(0.85)_saturate(1.5)_hue-rotate(-8deg)_contrast(1.05)_brightness(0.96)]"
                />
              </div>
              <div className="lg:hidden mt-6" data-testid="irs-factor-note-mobile">{factorNote}</div>
            </Reveal>

            <div className="mt-10 space-y-5" data-testid="irs-bars">
              {technology.irs.map((row, i) => (
                <Reveal key={row.label} delay={0.08 * i}>
                  <div>
                    <div className="flex justify-between items-baseline mb-2 gap-3">
                      <span className={`text-sm uppercase tracking-wide ${row.highlight ? "text-white font-bold" : "text-white/60"}`} style={{ fontFamily: "Space Grotesk" }}>
                        {row.label} <span className="text-white/60 normal-case tracking-normal">· {row.note}</span>
                      </span>
                      <span className="font-black text-2xl shrink-0" style={{ fontFamily: "Archivo", color: row.highlight ? (row.value > 50 ? "#22c55e" : brand.yellow) : "#fff" }}>
                        {row.value}
                      </span>
                    </div>
                    <div className="h-3 w-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full transition-all duration-1000"
                        style={{ width: `${(row.value / max) * 100}%`, background: row.highlight ? (row.value > 50 ? "#22c55e" : brand.yellow) : "rgba(255,255,255,0.35)" }}
                      />
                    </div>
                  </div>
                </Reveal>
              ))}

              <Reveal delay={0.26}>
                <div className={`transition-opacity duration-500 ${irs !== null ? "opacity-100" : "opacity-40"}`} data-testid="irs-result-bar">
                  <div className="flex justify-between items-baseline mb-2 gap-3">
                    <span className="text-sm uppercase tracking-wide font-bold" style={{ fontFamily: "Space Grotesk", color: irs !== null ? color : "#fff" }}>
                      {t("irs_custom")} <span className="text-white/60 normal-case tracking-normal">{irs !== null ? `· ${peso} kg / ${Math.round(w)}W · ×${factor}` : t("irs_insert_below")}</span>
                    </span>
                    <span className="font-black text-2xl shrink-0" style={{ fontFamily: "Archivo", color: irs !== null ? color : "rgba(255,255,255,0.25)" }}>
                      {irs !== null ? irs.toFixed(1) : "—"}
                    </span>
                  </div>
                  <div className="h-3 w-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full transition-all duration-700 ease-out"
                      style={{ width: irs !== null ? `${Math.min((irs / max) * 100, 100)}%` : "0%", background: irs !== null ? color : "transparent" }}
                    />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="hidden lg:col-span-7 lg:flex lg:flex-col lg:justify-center">
            <Reveal delay={0.15}>
              <div className="hidden lg:flex lg:flex-col lg:items-center">
                <div className="overflow-hidden max-w-[484px] w-full" data-testid="irs-image-desktop">
                  <img loading="lazy" decoding="async"
                    src="/img/ext/ciueoq69_IMG_5864-5439e3.webp"
                    alt="IRS frontale — stratigrafia del pannello fotovoltaico"
                    className="w-full h-auto object-contain transition-[transform,filter] duration-700 hover:scale-105 hover:[filter:sepia(0.85)_saturate(1.5)_hue-rotate(-8deg)_contrast(1.05)_brightness(0.96)]"
                  />
                </div>
                <div className="mt-8 w-full max-w-[484px]" data-testid="irs-factor-note-desktop">{factorNote}</div>
              </div>
            </Reveal>
          </div>
        </div>

          {collapsibleCalc ? (
            <div className="grid transition-[grid-template-rows] duration-500 ease-out" style={{ gridTemplateRows: calcOpen ? "1fr" : "0fr" }} data-testid="irs-calc-collapse">
              <div className="overflow-hidden">
                <IrsCalculator peso={peso} watt={watt} setPeso={setPeso} setWatt={setWatt} materiale={materiale} setMateriale={setMateriale} irs={irs} color={color} verdict={verdict} />
              </div>
            </div>
          ) : (
            <IrsCalculator peso={peso} watt={watt} setPeso={setPeso} setWatt={setWatt} materiale={materiale} setMateriale={setMateriale} irs={irs} color={color} verdict={verdict} />
          )}

          {/* Sottosezione — fondamento scientifico dell'IRS (inline se non collassabile) */}
          {showStudy && !collapsibleStudy && (
          <div data-testid="irs-study-wrap">
          <div>
          <Reveal>
            <div className="mt-10 lg:mt-14 pt-8 lg:pt-10 border-t border-white/10" data-testid="irs-study">
              <span className="text-white/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>{t("study_eyebrow")}</span>
              <div className="mt-5 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                <div className="lg:col-span-5">
                  <h3 className="font-black text-2xl md:text-3xl leading-tight tracking-tight" style={{ fontFamily: "Archivo" }}>{t("study_title")}</h3>
                  <p className="mt-5 text-white/55 leading-relaxed" style={{ fontFamily: "Inter" }}>
                    {t("study_p1")}
                  </p>
                  <p className="mt-4 text-white/55 leading-relaxed" style={{ fontFamily: "Inter" }}>
                    {t("study_p2")}
                  </p>
                  <a href="https://doi.org/10.1016/j.xcrp.2026.103208" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-start gap-2.5 text-xs text-white/60 hover:text-white/80 transition-colors leading-relaxed" style={{ fontFamily: "Space Grotesk" }} data-testid="irs-study-citation">
                    <BookOpen size={16} className="mt-0.5 shrink-0" style={{ color: brand.yellow }} />
                    <span>Vicari Stefani et al., «Increasing specific power and the emergence of new markets for crystalline silicon photovoltaics», <em>Cell Reports Physical Science</em> (2026) · DOI 10.1016/j.xcrp.2026.103208</span>
                  </a>
                </div>
                <div className="lg:col-span-7">
                  <div className="bg-white rounded-lg overflow-hidden border border-white/10 max-w-full lg:max-w-[60%] mx-auto">
                    <img loading="lazy" decoding="async" src="/tech/irs-study.jpeg?v=2" alt="Studio Cell Reports Physical Science sul rapporto massa/materiale nei moduli fotovoltaici" className="w-full h-auto block" data-testid="irs-study-img" />
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          </div>
          </div>
          )}
        </div>
        )}
      </div>

      {activeTech && <TechModal data={techModal[activeTech]} onClose={() => setActiveTech(null)} />}
      {showStudy && collapsibleStudy && studyOpen && <StudyModal onClose={() => setStudyOpen(false)} />}
    </section>
  );
}

function IrsCalculator({ peso, watt, setPeso, setWatt, materiale, setMateriale, irs, color, verdict }) {
  return (
    <Reveal>
      <div className="mt-8 pt-8 lg:mt-12 lg:pt-10 border-t border-white/10" data-testid="irs-calculator">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="text-white/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>
              {t("calc_eyebrow")}
            </span>
            <h3 className="mt-4 font-black text-2xl md:text-3xl leading-tight tracking-tight" style={{ fontFamily: "Archivo" }}>
              {t("calc_title")}
            </h3>
            <p className="mt-4 text-white/55 leading-relaxed text-sm" style={{ fontFamily: "Inter" }}>
              {t("calc_p")}
            </p>
          </div>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-start" data-testid="irs-controls-row">
              <label className="block">
                <span className="text-white/50 text-xs uppercase tracking-[0.14em]" style={{ fontFamily: "Space Grotesk" }}>{t("calc_peso")}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={peso}
                  onChange={(e) => setPeso(e.target.value)}
                  placeholder={t("calc_ph_peso")}
                  data-testid="irs-input-peso"
                  className="mt-2 w-full bg-transparent border border-white/20 focus:border-white/60 outline-none px-4 py-3 text-white text-lg font-bold transition-colors"
                  style={{ fontFamily: "Archivo" }}
                />
              </label>
              <label className="block">
                <span className="text-white/50 text-xs uppercase tracking-[0.14em]" style={{ fontFamily: "Space Grotesk" }}>{t("calc_watt")}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  value={watt}
                  onChange={(e) => setWatt(e.target.value)}
                  placeholder={t("calc_ph_watt")}
                  data-testid="irs-input-watt"
                  className="mt-2 w-full bg-transparent border border-white/20 focus:border-white/60 outline-none px-4 py-3 text-white text-lg font-bold transition-colors"
                  style={{ fontFamily: "Archivo" }}
                />
              </label>
              {[
                { key: "vetro", label: t("calc_mat_vv"), f: "×0,5" },
                { key: "polimero", label: t("calc_mat_vp"), f: "×0,95" },
              ].map((m, idx) => {
                const active = materiale === m.key;
                return (
                  <div key={m.key} className="block">
                    <span className="block whitespace-nowrap text-white/50 text-xs uppercase tracking-[0.14em]" style={{ fontFamily: "Space Grotesk" }}>
                      {idx === 0 ? t("calc_mat_front") : "\u00A0"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setMateriale(m.key)}
                      data-testid={`irs-material-${m.key}`}
                      className="mt-2 w-full px-4 py-3 text-left border transition-colors"
                      style={{
                        fontFamily: "Space Grotesk",
                        borderColor: active ? brand.yellow : "rgba(255,255,255,0.2)",
                        background: active ? `${brand.yellow}14` : "transparent",
                        color: active ? "#fff" : "rgba(255,255,255,0.6)",
                      }}
                    >
                      <span className="block text-sm font-semibold">{m.label}</span>
                      <span className="block text-xs mt-0.5" style={{ color: active ? brand.yellow : "rgba(255,255,255,0.35)" }}>{t("calc_factor")} {m.f}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 min-h-[64px]">
              {irs === null ? (
                <p className="text-white/60 text-sm" style={{ fontFamily: "Inter" }}>
                  {t("calc_insert_both")}
                </p>
              ) : (
                <>
                  <div className="flex items-baseline gap-3" data-testid="irs-result">
                    <span className="text-white/60 text-xs uppercase tracking-[0.14em]" style={{ fontFamily: "Space Grotesk" }}>IRS</span>
                    <span className="font-black text-4xl" style={{ fontFamily: "Archivo", color }}>{irs.toFixed(1)}</span>
                  </div>
                  <span
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"
                    data-testid="irs-verdict"
                    style={{ fontFamily: "Space Grotesk", color, border: `1px solid ${color}`, background: `${color}1a` }}
                  >
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                    {verdict}
                  </span>
                </>
              )}
            </div>

            <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:flex-nowrap lg:items-center lg:gap-x-6 text-xs" style={{ fontFamily: "Space Grotesk" }}>
              <span className="inline-flex items-center gap-2.5 text-white/70 lg:whitespace-nowrap"><span className="h-3 w-3 rounded-full shrink-0" style={{ background: "#ef4444" }} />&lt; 30 · {t("legend_fragile")}</span>
              <span className="inline-flex items-center gap-2.5 text-white/70 lg:whitespace-nowrap"><span className="h-3 w-3 rounded-full shrink-0" style={{ background: brand.yellow }} />30–50 · {t("legend_standard")}</span>
              <span className="inline-flex items-center gap-2.5 text-white/70 lg:whitespace-nowrap"><span className="h-3 w-3 rounded-full shrink-0" style={{ background: "#22c55e" }} />&gt; 50 · {t("legend_robust")}</span>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
